import React, { useRef, useState } from 'react';
import { deviceNow, newId } from './outbox';
import { smanji, telefonskoSkladiste } from './fotografije';

interface Props {
  tourId: number;
  accessKey: string;
  stopId: number;
  /** Koliko slika sa ove stanice još nije otišlo. */
  naCekanju: number;
  /** Slika je sačuvana na telefonu — roditelj osveži red i pokuša slanje. */
  onDodato: () => void;
}

/**
 * Fotografija sa stanice: oštećenje, odbijena roba, gde je roba ostavljena.
 *
 * Nije obavezna. Radi i bez mreže — slika se čuva na telefonu i šalje kad se
 * signal vrati. Napomena uz sliku je kratka i nije obavezna.
 */
export default function FotografijeStanice({ tourId, accessKey, stopId, naCekanju, onDodato }: Props) {
  const kamera = useRef<HTMLInputElement>(null);
  const [slika, setSlika] = useState<string | null>(null);
  const [napomena, setNapomena] = useState('');
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);

  const izabrana = async (fajl: File | undefined) => {
    if (!fajl) return;
    setGreska(null);
    setRadi(true);
    try {
      setSlika(await smanji(fajl));
      setNapomena('');
    } catch (e) {
      setGreska(e instanceof Error ? e.message : 'Slika se ne da pročitati.');
    } finally {
      setRadi(false);
      if (kamera.current) kamera.current.value = '';
    }
  };

  const sacuvaj = async () => {
    if (!slika) return;
    setRadi(true);
    try {
      await telefonskoSkladiste.sacuvaj({
        id: newId(), tourId, accessKey, stopId, takenAt: deviceNow(),
        note: napomena.trim() || undefined, image: slika
      });
      setSlika(null);
      onDodato();
    } catch (e) {
      // Ne guta se: vozač mora znati da slika NIJE sačuvana.
      setGreska(e instanceof Error ? e.message : 'Fotografija nije sačuvana na telefonu.');
    } finally {
      setRadi(false);
    }
  };

  return (
    <div className="dstop__foto">
      <input
        ref={kamera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid={`kamera-${stopId}`}
        onChange={(e) => void izabrana(e.target.files?.[0])}
      />

      {slika ? (
        <div className="dstop__beleska">
          <img className="dstop__foto-pregled" src={slika} alt="Nova fotografija" />
          <label htmlFor={`foto-napomena-${stopId}`}>Šta je na slici (nije obavezno)</label>
          <textarea
            id={`foto-napomena-${stopId}`}
            rows={2}
            value={napomena}
            onChange={(e) => setNapomena(e.target.value)}
            placeholder="npr. ogrebotina na vratima frižidera"
          />
          <div className="dstop__beleska-dugmad">
            <button type="button" className="primary" disabled={radi} onClick={() => void sacuvaj()}>
              Sačuvaj fotografiju
            </button>
            <button type="button" className="link" onClick={() => setSlika(null)}>
              Odustani
            </button>
          </div>
        </div>
      ) : (
        <button type="button" disabled={radi} onClick={() => kamera.current?.click()}>
          {radi ? 'Obrađujem…' : 'Fotografija'}
        </button>
      )}

      {naCekanju > 0 && (
        <span className="dstop__foto-ceka">
          {naCekanju === 1 ? '1 fotografija čeka slanje' : `${naCekanju} fotografija čeka slanje`}
        </span>
      )}

      {greska && <p className="dstop__exception">{greska}</p>}
    </div>
  );
}
